import { useContext } from 'react';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import {
  Button,
  Popover,
  Spinner,
  SpinnerSize,
  Tooltip,
} from '@blueprintjs/core';
import { UserName } from '../../UserName';
import styles from './LockTag.module.css';
import { loan_application } from '@axo/shared/data-access/types';
import { lock_hooks } from '@axo/shared/data-access/hooks';
import { SmallTag } from '../../SmallTag';

export function LockTag({
  marketCountry,
  app,
}: {
  marketCountry: string;
  app: loan_application.LoanApplication;
}) {
  const {
    state: {
      user: { clientID },
    },
  } = useContext(DataAccessContext);
  const currentLock = lock_hooks.useApplicationLock(app.ID, marketCountry);
  const isUnlocked = currentLock.isSuccess && currentLock.data === null;

  return (
    <span>
      {currentLock.isLoading && <Spinner size={SpinnerSize.SMALL} />}
      {currentLock.data && (
        <>
          {currentLock.data.LockedBy !== clientID ? (
            <Tooltip
              placement="bottom"
              minimal
              content={
                <span>
                  Application is locked by{' '}
                  <strong>
                    <UserName clientID={currentLock.data.LockedBy} />
                  </strong>{' '}
                  and is therefore read-only.
                </span>
              }
            >
              <SmallTag icon="eye-open" intent="warning">
                <UserName clientID={currentLock.data.LockedBy} />
              </SmallTag>
            </Tooltip>
          ) : (
            <Tooltip content="Locked by you">
              <SmallTag icon="lock" minimal intent={'success'}>
                <UserName clientID={currentLock.data.LockedBy} />
              </SmallTag>
            </Tooltip>
          )}
        </>
      )}
      {isUnlocked && (
        <Popover
          interactionKind="hover"
          content={
            <div className={styles.popoverContent}>
              <p>
                The application is unlocked and is therefore read-only. Lock to
                edit.
              </p>
              {app && <LockButton application={app} />}
            </div>
          }
        >
          <SmallTag icon="eye-open" intent="none" minimal>
            Unlocked
          </SmallTag>
        </Popover>
      )}
    </span>
  );
}

function LockButton({
  application,
}: {
  application: loan_application.LoanApplication;
}) {
  const acquireLock = lock_hooks.useLockApplication();

  return (
    <Button
      icon="lock"
      loading={acquireLock.isLoading}
      onClick={() =>
        acquireLock.mutate({
          applicationID: application.ID,
          marketCountry: application.MarketCountry,
        })
      }
    >
      Acquire lock
    </Button>
  );
}
